import { useEffect, useRef } from "react";

// Keys the Ant Select owns: navigation through options, selection, dismissal.
// AntD 6 binds its combobox keyboard handling to the Select's own (readonly)
// input, and our search box holds focus for as long as the popup is open — so
// these never arrive on their own, no matter how far they bubble. Forward them
// to that input instead. rc-select reads `keyCode`, which KeyboardEventInit
// ignores, so it has to be defined onto the event by hand.
// Only the keys rc-select actually acts on. Home/End/PageUp/PageDown are
// deliberately absent: rc-select ignores them, so forwarding them would only
// cost the user caret-jumps inside the search box.
const FORWARDED_KEYS: Record<string, number> = {
    ArrowUp: 38,
    ArrowDown: 40,
    Enter: 13,
    Escape: 27,
};

// Caret and focus keys, left to bubble untouched — the Select does not act on
// these, and the search box needs them. Everything else (letters, digits,
// backspace…) is stopped so typing reaches our search input instead of the
// Select's built-in type-ahead.
const PASS_THROUGH_KEYS = new Set([
    "ArrowLeft",
    "ArrowRight",
    "Tab",
    "Home",
    "End",
    "PageUp",
    "PageDown",
]);

interface CountrySearchPopupProps {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    children: React.ReactNode;
    ariaLabel?: string;
    wrapperClassName?: string;
    inputClassName?: string;
}

/**
 * Search input + menu wrapper used inside Ant Select's popupRender across
 * every country/phone-code picker. Handles:
 *   - auto-focus each time the popup becomes visible (via IntersectionObserver,
 *     since antd keeps popupRender content mounted but hidden between opens
 *     and does NOT re-render it while hidden — so prop-driven focus can't work)
 *   - synthetic mouseDown before focus to prevent antd 6's lockOptions from
 *     freezing the option list (rc-select locks options when the Select's
 *     internal input blurs; the mouseDown sets internalMouseDownRef which
 *     tells the blur handler to skip the lock)
 *   - onMouseDown guard: stop propagation only when the user clicks the
 *     input, so the Select doesn't steal focus; option clicks still bubble
 *   - onKeyDown guard: stop character keys from reaching the Select's
 *     type-ahead, and forward Arrow/Enter/Escape to the Select's own input so
 *     the list can be driven from the search box without touching the mouse
 */
const CountrySearchPopup: React.FC<CountrySearchPopupProps> = ({
    value,
    onChange,
    placeholder,
    children,
    ariaLabel,
    wrapperClassName = "country-code-options-wrapper",
    inputClassName = "country-search-input mb-8",
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setTimeout(() => {
                        wrapperRef.current?.dispatchEvent(
                            new MouseEvent("mousedown", { bubbles: true }),
                        );
                        inputRef.current?.focus({ preventScroll: true });
                    }, 0);
                }
            },
            { threshold: 0.1 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // The popup is portalled, so the owning Select can't be reached by walking
    // up the DOM. rc-select ids the option list `<selectInputId>_list` and
    // points the open Select's input at it via aria-controls, which gives us
    // the input back — by relationship rather than by id, so two Selects that
    // happen to share a field name can't cross wires. Falling back to the id
    // keeps this working if that attribute ever moves.
    // Returns false when the Select can't be found, so the caller can leave the
    // key alone rather than eating it — an antd change to the id scheme should
    // cost us the forwarding, not the keypress.
    const forwardKeyToSelect = (key: string): boolean => {
        const list = wrapperRef.current
            ?.closest(".ant-select-dropdown")
            ?.querySelector('[id$="_list"]');
        if (!list) return false;
        const selectInput =
            document.querySelector<HTMLElement>(
                `[aria-controls="${list.id}"]`,
            ) || document.getElementById(list.id.replace(/_list$/, ""));
        if (!selectInput) return false;
        const keyCode = FORWARDED_KEYS[key];
        const send = (type: "keydown" | "keyup") => {
            const event = new KeyboardEvent(type, {
                key,
                bubbles: true,
                cancelable: true,
            });
            Object.defineProperty(event, "keyCode", { get: () => keyCode });
            Object.defineProperty(event, "which", { get: () => keyCode });
            selectInput.dispatchEvent(event);
        };
        send("keydown");
        // rc-select latches an Enter lock on keydown and only releases it on
        // keyup or blur. The real keyup lands on our search box, so without
        // this the lock would clear only as a side effect of the popup
        // stealing focus on the next open — and Enter would die the moment
        // that ordering changed.
        send("keyup");
        return true;
    };

    return (
        <div
            ref={wrapperRef}
            className={wrapperClassName}
            onMouseDown={(e) => {
                if ((e.target as HTMLElement).tagName === "INPUT") {
                    e.stopPropagation();
                }
            }}
        >
            <input
                ref={inputRef}
                role="searchbox"
                aria-autocomplete="list"
                className={inputClassName}
                placeholder={placeholder}
                aria-label={ariaLabel ?? placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (Object.hasOwn(FORWARDED_KEYS, e.key)) {
                        // Only claim the key once the Select has actually been
                        // handed it: Enter must not reach the surrounding form
                        // and the arrows must not move the caret. If the
                        // Select couldn't be found, leave the key completely
                        // alone rather than swallowing it.
                        if (forwardKeyToSelect(e.key)) e.preventDefault();
                        return;
                    }
                    if (!PASS_THROUGH_KEYS.has(e.key)) {
                        e.stopPropagation();
                    }
                }}
            />
            {children}
        </div>
    );
};

export default CountrySearchPopup;
